import { type HTMLAttributes, type ReactNode } from 'react';
import { cn } from '@/shared/lib/cn';
import './PageHeader.css';

/**
 * MIGRACIÓN: se agregó `Omit<..., 'title'>`. `HTMLAttributes` declara
 * `title?: string` (el tooltip nativo) y aquí `title` es un ReactNode: sin
 * el Omit, TS estricto rechaza la interfaz. El sistema viejo compilaba con
 * una configuración más laxa; el componente en sí no cambió.
 */
export interface PageHeaderProps extends Omit<HTMLAttributes<HTMLElement>, "title"> {
  title: ReactNode;
  description?: ReactNode;
  breadcrumbs?: ReactNode;
  actions?: ReactNode;
  /** Chip pill pequeño arriba del título (ej: "KYC nivel 2") */
  label?: ReactNode;
  /** Agrega border-bottom separador */
  divider?: boolean;
  /**
   * Título más chico y menos aire debajo.
   *
   * Es para las pantallas que se recorren por pasos: ahí el encabezado
   * es el mismo en los diez y lo único que cambia es lo que hay debajo,
   * así que su alto se paga diez veces y sale de la pantalla lo que
   * importa.
   */
  compact?: boolean;
  /**
   * Oculta título y rótulo A LA VISTA, conservándolos para el lector de
   * pantalla.
   *
   * Es para las pantallas donde la barra superior ya dice el nombre: verlo
   * dos veces en la misma pantalla es ruido. Pero borrarlo del árbol
   * dejaría la página sin `<h1>`, y quien navega por encabezados perdería
   * el punto de entrada.
   */
  titleHidden?: boolean;
}

/**
 * Header grande al inicio de cada página (layout guide §7).
 * Un solo PageHeader por página. No usar en la home del perfil si el
 * Topbar ya tiene `greeting` con el título principal.
 */
export function PageHeader({
  title,
  description,
  breadcrumbs,
  actions,
  label,
  divider = false,
  compact = false,
  titleHidden = false,
  className,
  ...rest
}: PageHeaderProps) {
  return (
    <header
      className={cn(
        'itb-page-header',
        divider && 'itb-page-header--divider',
        compact && 'itb-page-header--compact',
        className,
      )}
      {...rest}
    >
      {breadcrumbs && <div className="itb-page-header__breadcrumbs">{breadcrumbs}</div>}
      {label && !titleHidden && (
        <span className="itb-page-header__label">{label}</span>
      )}

      {/*
        Con `titleHidden`, el título sale de la vista pero NO del árbol: la
        página conserva su `<h1>` para quien navega por encabezados. Las
        acciones sí se quedan visibles — el botón de "Nuevo producto" no
        estorba, lo que estorbaba era leer "Productos" dos veces.
      */}
      {titleHidden ? (
        /*
          Sin título visible, la descripción SUBE a la fila de las acciones.
          Si no, el botón se quedaba solo y pegado a la izquierda, con la
          descripción debajo — y las acciones van a la derecha, como en el
          encabezado normal (Anahí, 26-ago).

          La fila se alinea al final y la descripción se empuja con
          `margin-right:auto`: así el botón queda a la derecha tanto si hay
          descripción como si no.
        */
        <>
          <h1 className="sr-only">{title}</h1>
          <div className="itb-page-header__row itb-page-header__row--end">
            {description && (
              <p className="itb-page-header__desc itb-page-header__desc--inline">
                {description}
              </p>
            )}
            {actions && <div className="itb-page-header__actions">{actions}</div>}
          </div>
        </>
      ) : (
        <div className="itb-page-header__row">
          <h1 className="itb-page-header__title">{title}</h1>
          {actions && <div className="itb-page-header__actions">{actions}</div>}
        </div>
      )}
      {description && !titleHidden && (
        <p className="itb-page-header__desc">{description}</p>
      )}
    </header>
  );
}
